import {
  PICKUP_OPTIMYZER_FILTERS,
  PICKUP_OPTIMYZER_FILTER_NAME,
} from "./constants";
import {
  isNotAssignedPickup,
  isNotCompletedPickup,
} from "../../../modules/Dispatch/RouteExecution/filterUtils";
import { uniq } from "lodash";
import { findDriverRoute } from "../../../modules/Dispatch/RouteExecution/Recommendations/common/utils";

const getDefaultFilters = () => {
  return [
    {
      name: PICKUP_OPTIMYZER_FILTER_NAME.isAssigned,
      label: PICKUP_OPTIMYZER_FILTERS.assigned,
      filterFunc: ({pickup, terminalOffset}) => isNotCompletedPickup({pickup, terminalOffset}),
    },
    {
      name: PICKUP_OPTIMYZER_FILTER_NAME.isUnassigned,
      label: PICKUP_OPTIMYZER_FILTERS.unassigned,
      filterFunc: ({pickup, terminalOffset}) => isNotAssignedPickup({pickup, terminalOffset}),
    },
  ];
};

const getDefaultSelectedFilters = () => {
  return {
    [PICKUP_OPTIMYZER_FILTER_NAME.isAssigned]: false,
    [PICKUP_OPTIMYZER_FILTER_NAME.isUnassigned]: true,
    [PICKUP_OPTIMYZER_FILTER_NAME.excludeSwapPickups]: true,
  };
};

const getSelectedPickupsByPickupId = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  return pickupListForOptimization.filter((pickup) =>
    selectedPickupIdList.includes(pickup.pickupId.toLowerCase())
  );
};

const isUnassignedPickupSelected = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  return pickupListForOptimization.some(
    (pickup) =>
      selectedPickupIdList.includes(pickup.pickupId.toLowerCase()) &&
      !pickup.routeId
  );
};

const isPendingAssignedPickupSelected = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  return pickupListForOptimization.some(
    (pickup) =>
      selectedPickupIdList.includes(pickup.pickupId.toLowerCase()) &&
      pickup.routeId
  );
};

const getAllUnassignedPickups = (pickupsList) => {
  return pickupsList.filter((pickup) => !pickup.routeId);
};

const getAllAssignedPickups = (pickupsList) => {
  return pickupsList.filter((pickup) => pickup.routeId);
};

const getShowPickupsListWhenPickupsSelectedInGrid = (
  pickupListForOptimization,
  selectedPickupIdList
) => {
  const assignedPickupSelected = isPendingAssignedPickupSelected(
    pickupListForOptimization,
    selectedPickupIdList
  );
  if (
    assignedPickupSelected &&
    isUnassignedPickupSelected(pickupListForOptimization, selectedPickupIdList)
  ) {
    return pickupListForOptimization;
  }
  if (assignedPickupSelected) {
    return getAllAssignedPickups(pickupListForOptimization);
  }
  return getAllUnassignedPickups(pickupListForOptimization);
};

const countAssignedPickups = (acc, pickup) =>
  pickup.routeId && pickup.driverId ? acc + 1 : acc;

const countUnassignedPickups = (acc, pickup) =>
  !pickup.routeId && !pickup.driverId ? acc + 1 : acc;

const getTotalAssignedPickups = (pickupsList) => {
  return pickupsList.reduce(countAssignedPickups, 0);
};

const getTotalUnassignedPickups = (pickupsList) => {
  return pickupsList.reduce(countUnassignedPickups, 0);
};

const getTotalAssignedPickupsSelected = (selectedPickupsList) => {
  return selectedPickupsList.reduce(countAssignedPickups, 0);
};

const getTotalUnassignedPickupsSelected = (selectedPickupsList) => {
  return selectedPickupsList.reduce(countUnassignedPickups, 0);
};

const isPartialAssignedPickupsSelected = (selectedPickupsList, pickupsList) => {
  const totalAssignedPickupsSelected =
    getTotalAssignedPickupsSelected(selectedPickupsList);
  return (
    totalAssignedPickupsSelected > 0 &&
    totalAssignedPickupsSelected < getTotalAssignedPickups(pickupsList)
  );
};

const isPartialUnassignedPickupsSelected = (
  selectedPickupsList,
  pickupsList
) => {
  const totalUnassignedPickupsSelected =
    getTotalUnassignedPickupsSelected(selectedPickupsList);
  return (
    totalUnassignedPickupsSelected > 0 &&
    totalUnassignedPickupsSelected < getTotalUnassignedPickups(pickupsList)
  );
};

const isPickupSelectedInGrid = (selectedPickupIdList) => {
  return selectedPickupIdList.length;
};

const getReassignPickupDetail = (selectedPickupsToOptimize) => {
  return selectedPickupsToOptimize.reduce((acc, pickup) => {
    return [
      ...acc,
      ...(pickup.isUnassignedPickup
        ? [
            {
              groupId: pickup.groupId,
              pickupId: pickup.pickupIdList,
              driverIdToUnassign: pickup.previousDriver?.driverId ?? null,
              driverIdToAssign: pickup.driverId,
            },
          ]
        : pickup.stops.map((stop) => ({
            groupId: stop.groupId,
            pickupId: stop.pickupIdList,
            driverIdToUnassign: stop.previousDriver?.driverId ?? null,
            driverIdToAssign: stop.driverId,
          }))),
    ];
  }, []);
};

const getSelectedPickupsToOptimizationCount = (selectedPickupsToOptimize) => {
  return selectedPickupsToOptimize.reduce((acc, selectedPickup) => {
    if (selectedPickup.isUnassignedPickup) {
      return acc + 1;
    }
    return acc + selectedPickup.stops.length;
  }, 0);
};

const getAvailablePickupForOptimization = (driverAssignmentsList) => {
  return driverAssignmentsList.reduce((acc, driverAssignment) => {
    if (driverAssignment.driverId && driverAssignment) {
      return (
        acc + driverAssignment.stops.filter((stop) => stop.isVirtualStop).length
      );
    }
    return acc + 1;
  }, 0);
};

const getDriverPickupIds = (driverAssignment) => {
  return driverAssignment.map((route) => route.groupIds).flat();
};

const getSelectedDriverRouteBasedOnPickup = (
  driverAssignmentsList,
  selectedPickupId
) => {
  return driverAssignmentsList.filter(
    (driverAssignment) =>
      driverAssignment.driverId &&
      driverAssignment.groupIds.some((v) => selectedPickupId.includes(v))
  );
};

const getOptimizeSolutionWithDriver = (driverAssignmentsList) => {
  return driverAssignmentsList.filter(
    (driverAssignment) => driverAssignment.driverId
  );
};

const selectedDriverAssignments = (
  driverAssignmentList,
  selectedDriverAssignmentId
) => {
  return driverAssignmentList.filter((driverAssignment) =>
    selectedDriverAssignmentId.includes(driverAssignment.driverId)
  );
};

const getSelectedStopsList = ({
  selectedDriverAssignment,
  selectedPickupId,
  routesList,
}) => {
  return selectedDriverAssignment.map((route) => ({
    ...route,
    stops: route.stops.filter((stop) => {
      return selectedPickupId.includes(stop.groupId?.toLowerCase());
    }),
    previousDriver: route.previousDriverId
      ? findDriverRoute(routesList, routeStop.previousDriverId)
      : null,
  }));
};

const removePickupIdFromList = (pickupIdList, pickupId) => {
  return pickupIdList.filter((pickup) => pickup.pickupId !== pickupId);
};

const getKPIDetails = (reassignPickupDetail, startTime) => {
  const totalPickupsAssigned = reassignPickupDetail.reduce((total, pickup) => {
    return total + (Array.isArray(pickup.pickupId) ? pickup.pickupId.length : 1);
  }, 0);
  
  return {
    timeRequired: convertMillisecondIntoMin(new Date() - startTime),
    pickupsAssigned: totalPickupsAssigned,
    driversInvolved: getDriversInvolvedCount(reassignPickupDetail),
  };
};

const getDriversInvolvedCount = (reassignPickupDetail) => {
  return uniq(
    reassignPickupDetail.reduce((driverList, pickup) => {
      return pickup.driverIdToUnassign
        ? [pickup.driverIdToAssign, pickup.driverIdToUnassign, ...driverList]
        : [pickup.driverIdToAssign, ...driverList];
    }, [])
  ).length;
};

const convertMillisecondIntoMin = (milliseconds) => {
  const date = new Date(Date.UTC(0, 0, 0, 0, 0, 0, milliseconds));
  const parts = [];
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  hours && parts.push(`${hours} hour `);
  minutes && parts.push(`${minutes} min `);
  seconds && parts.push(`${seconds} sec `);
  return parts.join(" ");
};

const getOccupiedCapacity = (estimatedTrailerSpaceRemaining, maxSkid) => {
  const estimatedSpace = estimatedTrailerSpaceRemaining ?? 0;
  const occupied = maxSkid - estimatedSpace;
  return `${parseFloat(Number(occupied).toFixed(2))} / ${maxSkid}`;
};

export {
  getDefaultFilters,
  getDefaultSelectedFilters,
  getSelectedPickupsByPickupId,
  isUnassignedPickupSelected,
  getShowPickupsListWhenPickupsSelectedInGrid,
  getAllUnassignedPickups,
  isPendingAssignedPickupSelected,
  isPartialUnassignedPickupsSelected,
  isPartialAssignedPickupsSelected,
  getAllAssignedPickups,
  isPickupSelectedInGrid,
  getReassignPickupDetail,
  getDriverPickupIds,
  getSelectedDriverRouteBasedOnPickup,
  getOptimizeSolutionWithDriver,
  selectedDriverAssignments,
  getSelectedStopsList,
  removePickupIdFromList,
  getSelectedPickupsToOptimizationCount,
  getAvailablePickupForOptimization,
  getKPIDetails,
  getOccupiedCapacity,
};
